/**
 * Модальное окно на родном `<dialog>`.
 *
 * Родной элемент взят не из экономии: он один даёт верхний слой над всем,
 * захват фокуса, закрытие по Escape и подложку без единой строки скрипта. Всё
 * это в самодельном окне приходится писать руками, и обычно пишут неправильно —
 * фокус уходит в страницу под окном, а Escape закрывает не то.
 *
 * Что остаётся на нас (app-kit.md §4): возврат фокуса на кнопку, которая окно
 * открыла, запрет прокрутки страницы под ним и кнопка подтверждения, которая
 * **называет действие** — «Отозвать доступ», а не «ОК».
 *
 * Закрытия кликом по подложке здесь нет намеренно. У окна подтверждения оно
 * опасно: случайный клик мимо отменяет действие, которое человек уже собрался
 * подтвердить, — и молча, без объяснения. Выходов два и оба явные: Escape и
 * кнопка отмены.
 */

import { useEffect, useRef, type ReactNode } from 'react';

export function Dialog({
  open,
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  open: boolean;
  title: string;
  /** Escape, клик по подложке, крестик — один и тот же выход. */
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Окно с таблицей, а не с формой. Подтверждение и поле помещаются в 560px, а
   * список вопросов в такой ширине переносится на каждом слове: сравнивать
   * строки в столбце из огрызков нельзя.
   */
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  // Кто открыл окно — туда и вернём фокус. Иначе после закрытия он оказывается
  // в начале страницы, и человек с клавиатуры проходит меню заново.
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (open && !node.open) {
      opener.current = document.activeElement;
      node.showModal();
      document.body.style.overflow = 'hidden';
    }
    if (!open && node.open) {
      node.close();
      document.body.style.overflow = '';
      if (opener.current instanceof HTMLElement) opener.current.focus();
    }
  }, [open]);

  // Окно может исчезнуть не закрывшись — вместе с экраном или потому, что его
  // перестали рисовать. Тогда ветка `!open && node.open` не отработает никогда,
  // и фокус останется висеть на удалённом узле, то есть уедет в начало
  // страницы. Возврат фокуса и снятие блокировки прокрутки продублированы в
  // размонтировании: два пути закрытия — две уборки.
  useEffect(
    () => () => {
      document.body.style.overflow = '';
      if (opener.current instanceof HTMLElement) opener.current.focus();
    },
    [],
  );

  return (
    <dialog
      ref={ref}
      className={wide ? 'dlg wide' : 'dlg'}
      aria-label={title}
      // Escape приходит сюда событием `cancel`: перехватываем его, чтобы
      // закрытие шло через то же состояние, что и кнопка отмены, а не мимо него.
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="dlg-in">
        <h2>{title}</h2>
        {children}
        {footer ? <div className="dlg-foot">{footer}</div> : null}
      </div>
    </dialog>
  );
}
